import React, { FC, useEffect, useMemo, useState } from "react";
import CtaBubble from "./CtaBubble";
import { log } from "../../utils";

export interface Props {
    readonly visible: boolean;
    /**
     * How long to display the bubble, in MS
     */
    readonly timeout?: number;
    /**
     * How long to delay showing the bubble, in MS
     */
    readonly delay?: number;
    readonly animate?: boolean;
    readonly buttonAnimation?: string;
    readonly dismissible?: boolean;
    onClick?: () => void;
    onDismiss?: () => void;
}

const CtaBubbleContainer: FC<React.PropsWithChildren<Props>> = (props) => {
    const {
        visible,
        timeout,
        delay,
        animate,
        buttonAnimation,
        dismissible,
        onClick,
        onDismiss,
        children,
    } = props;

    // Debug: Log all props received
    log("[CtaBubbleContainer] Props received:", {
        visible,
        timeout,
        delay,
        animate,
        buttonAnimation,
        dismissible,
        hasOnClick: !!onClick,
        hasOnDismiss: !!onDismiss,
        hasChildren: !!children,
        childrenContent: children,
    });

    const startTime = useMemo<number | undefined>(() => {
        const time = visible ? new Date().valueOf() : undefined;
        log(
            `[CtaBubbleContainer] StartTime calculated: visible=${visible}, startTime=${time}`
        );
        return time;
    }, [visible]);

    const [showBubble, setShowBubble] = useState(false);
    log(`[CtaBubbleContainer] Current state: showBubble=${showBubble}`);

    useEffect(() => {
        log("[CtaBubbleContainer] useEffect triggered with:", {
            startTime,
            startTimeExists: !!startTime,
            timeout,
            timeoutType: typeof timeout,
            delay,
            delayType: typeof delay,
            visible,
        });
        let hideTimer: NodeJS.Timeout | null = null;
        let delayTimer: NodeJS.Timeout | null = null;
        let isMounted = true;

        if (startTime) {
            const actualDelay = delay || 0;
            log(
                `[CtaBubbleContainer] Timer setup: actualDelay=${actualDelay}ms (from delay=${delay})`
            );

            delayTimer = setTimeout(() => {
                log(
                    `[CtaBubbleContainer] Delay timer fired after ${actualDelay}ms, isMounted=${isMounted}`
                );
                if (isMounted) {
                    log(
                        "[CtaBubbleContainer] Component still mounted, setting showBubble=true"
                    );
                    setShowBubble(true);

                    if (typeof timeout === "number") {
                        log(
                            `[CtaBubbleContainer] Hide timer setup: will hide after ${timeout}ms`
                        );
                        hideTimer = setTimeout(() => {
                            log(
                                `[CtaBubbleContainer] Hide timer fired after ${timeout}ms, isMounted=${isMounted}`
                            );
                            if (isMounted) {
                                log(
                                    "[CtaBubbleContainer] Component still mounted, setting showBubble=false"
                                );
                                setShowBubble(false);
                            } else {
                                log(
                                    "[CtaBubbleContainer] Component unmounted, skipping hide"
                                );
                            }
                        }, timeout);
                    } else {
                        log(
                            `[CtaBubbleContainer] No hide timer set (timeout=${timeout}, type=${typeof timeout})`
                        );
                    }
                } else {
                    log(
                        "[CtaBubbleContainer] Component unmounted before delay timer, not showing bubble"
                    );
                }
            }, actualDelay);
        } else {
            log("[CtaBubbleContainer] No startTime, skipping timer setup");
        }

        return () => {
            log("[CtaBubbleContainer] Cleanup triggered, unmounting component");
            isMounted = false;
            if (hideTimer) {
                log("[CtaBubbleContainer] Clearing hide timer");
                clearTimeout(hideTimer);
            }
            if (delayTimer) {
                log("[CtaBubbleContainer] Clearing delay timer");
                clearTimeout(delayTimer);
            }
        };
    }, [startTime, timeout, delay, visible]);

    const handleDismiss = () => {
        log("[CtaBubbleContainer] handleDismiss called, setting showBubble=false");
        setShowBubble(false);
        if (onDismiss) {
            log("[CtaBubbleContainer] Calling onDismiss callback");
            onDismiss();
        }
    };

    // Final render decision
    const shouldRender = visible && showBubble;
    log(
        `[CtaBubbleContainer] Render decision: visible=${visible}, showBubble=${showBubble}, shouldRender=${shouldRender}`
    );

    return (
        <>
            {visible && showBubble && (
                <CtaBubble
                    onClick={onClick}
                    animate={animate}
                    buttonAnimation={buttonAnimation}
                    dismissible={dismissible}
                    onDismiss={handleDismiss}>
                    {children}
                </CtaBubble>
            )}
        </>
    );
};

export default CtaBubbleContainer;
